import { Hero } from '@/components/sections/Hero';
import { ContactForm } from '@/components/forms/ContactForm';
import { EmailMessage } from '@/illustrations/EmailMessage';
import { Mail, Phone, MapPin, Clock, Check, LinkedIn } from '@/icons';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { FaqSection } from '@/components/sections/FaqSection';
import { contactFaq } from '@/data/faq';
import { briefChecklist, studioFacts } from '@/data/studio';
import { useSeo } from '@/lib/seo';

const directContacts = [
  {
    icon: Mail,
    label: 'Email',
    value: 'business@justsee-ltd.com',
    href: 'mailto:business@justsee-ltd.com',
  },
  {
    icon: Phone,
    label: 'Phone',
    value: '+35795904930',
    href: 'tel:+35795904930',
  },
  {
    icon: MapPin,
    label: 'Studio',
    value: studioFacts.addressLines.join('\n'),
    href: null,
  },
  {
    icon: Clock,
    label: 'Response time',
    value: 'Within one business day',
    href: null,
  },
  {
    icon: LinkedIn,
    label: 'LinkedIn',
    value: 'View profile',
    href: 'https://www.linkedin.com/in/vladislava-barkevica-kovaļenko-b9a5a0405?utm_source=share_via&utm_content=profile&utm_medium=member_ios',
  },
];

export default function Contact() {
  useSeo({
    title: 'Contact',
    description: 'Start a project with JUST SEE LTD. Write to us by form or email - we reply within one business day.',
  });

  return (
    <>
      <Hero
        eyebrow="Contact"
        headline={<>Tell us what you need.</>}
        description="Fill the form or email us directly. One business day to a reply - usually faster."
        illustration={<EmailMessage className="w-full max-w-md" />}
      />

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 border-t border-border">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Before you write" title="What to include in your brief." description="The more of these you have ready, the faster we can give you a real quote." />
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            {briefChecklist.map((item) => (
              <li key={item} className="flex gap-3 text-ink-secondary">
                <Check className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[1.2fr_0.8fr] gap-14 lg:gap-20">
          <div>
            <p className="mono-meta text-ink-muted mb-4">
              Prefer a direct line? Email{' '}
              <a className="text-accent underline underline-offset-4" href="mailto:business@justsee-ltd.com">
                business@justsee-ltd.com
              </a>{' '}
              - that lands in the same inbox as this form.
            </p>
            <ContactForm />
          </div>
          <aside className="flex flex-col gap-10 lg:sticky lg:top-32 lg:self-start">
            <SectionHeading eyebrow="Direct contacts" title="Or write the old way." />
            <ul className="flex flex-col gap-6">
              {directContacts.map(({ icon: Icon, label, value, href }) => {
                const isExternal = href?.startsWith('http');
                return (
                  <li key={label} className="flex gap-4">
                    <Icon className="w-6 h-6 text-accent shrink-0 mt-1" />
                    <div className="flex flex-col gap-1">
                      <span className="mono-meta text-ink-muted">{label}</span>
                      {href ? (
                        <a
                          className="text-lg font-sans hover:text-accent transition-colors break-words"
                          href={href}
                          {...(isExternal && { target: '_blank', rel: 'noopener noreferrer' })}
                        >
                          {value}
                        </a>
                      ) : (
                        <span className="text-lg font-sans whitespace-pre-line">{value}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-col gap-3 pt-6 border-t border-border">
              <div className="flex flex-col gap-1">
                <span className="mono-meta text-ink-muted">Working hours</span>
                <span className="text-sm text-ink-primary">{studioFacts.workingHours}</span>
                <span className="text-xs text-ink-muted">{studioFacts.timezone}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="mono-meta text-ink-muted">Confidentiality</span>
                <span className="text-sm text-ink-primary">NDAs signed on request, at no extra cost.</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="mono-meta text-ink-muted">Registration</span>
                <span className="text-sm text-ink-primary">{studioFacts.registrationCountry} · {studioFacts.registrationNumber}</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <FaqSection title="Before you write." items={contactFaq} />
    </>
  );
}
